import {Component, OnInit} from '@angular/core';
import {ValidatedForm} from "../../../forms/validated-form";
import {FormBuilder, Validators} from "@angular/forms";
import {StateService} from "../../../services/state.service";
import {Router} from "@angular/router";
import {Promo, PromoService} from "../../../services/promo.service";
import {DateTime, Duration} from 'luxon';
import {formatNumber} from "@angular/common";
import {AsyncValidatorFactoryService} from "../../../services/async-validator-factory.service";
import {Observable} from "rxjs";
import {finalize} from "rxjs/operators";
import {UtilService} from "../../../services/util.service";

const MIN_PROMO_LENGTH = 4;

@Component({
    templateUrl: './promo.component.html',
    styleUrls: ['./promo.component.css']
})
export class PromoComponent extends ValidatedForm implements OnInit {

    promo: Promo;

    constructor(protected fb: FormBuilder,
                private router: Router,
                private asyncValidatorService: AsyncValidatorFactoryService,
                private promoService: PromoService,
                private utilService: UtilService,
                private state: StateService) {
        super(fb);

        this.promo = this.state.get('promo');
        let tsExpires;
        if (this.promo.expires)
            tsExpires = DateTime.fromSQL(this.promo.expires);
        // Default to tomorrow at 00:00, client local time
        else
            tsExpires = DateTime.local().plus(Duration.fromISO('P1D')).startOf('day');
        const date: Date = tsExpires.toJSDate();
        const hour = tsExpires.hour;
        const hour12 = (hour + 11) % 12 + 1;
        const minute = formatNumber(tsExpires.minute, 'en-us', '2.0-0');
        const ampm = hour < 12 ? 'am' : 'pm';
        this.initForm({
            code: {
                value: this.promo.code,
                validators: [Validators.minLength(MIN_PROMO_LENGTH)],
                asyncValidators: [this.asyncValidatorService.getPromoCodeAvailableValidator(null, this.promo.code)],
                messages: {minlength: `At least ${MIN_PROMO_LENGTH} characters`, codeTaken: "Code is already in use"}
            },
            type: { value: this.promo.type },
            discount: { value: this.promo.discount },
            limited: { value: this.promo.initial_inventory > 0 },
            initialInventory: { value: this.promo.initial_inventory },
            expFlag: { value: !!this.promo.expires },
            expDate: { value: date },
            expHour: { value: hour12 },
            expMinute: { value: minute},
            expAmPm: { value: ampm },
            disabled: { value: this.promo.disabled }
        });
    }

    ngOnInit(): void {
    }

    codeChanged() {
        const control = this.form.controls.code;
        let text:string = control.value;
        // Remove any spaces and convert to uppercase
        text = text.replace(/\s/g,'').toUpperCase();
        this.form.patchValue({code: text});
    }


    submit() {
        let p: Promo = {
            id: this.promo.id,
            code: this.fields.code.value,
            type: this.fields.type.value,
            discount: this.fields.discount.value,
            inventory: this.promo.inventory
        };
        p.initial_inventory = this.fields.limited.value? this.fields.initialInventory.value : 0;
        // Top up (or limit) inventory if we changed limit
        if (p.initial_inventory != this.promo.initial_inventory)
            p.inventory = p.initial_inventory;
        p.disabled = this.fields.disabled.value;
        p.expires = null;
        if (this.fields.expFlag.value) {
            const isPm: boolean = this.fields.expAmPm.value == 'pm';
            const hour12 = +this.fields.expHour.value;
            const hour24 = isPm? (hour12 < 12? hour12 + 12 : 12) : (hour12 < 12? hour12 : 0);
            const expDateTime: DateTime = DateTime.fromJSDate(this.fields.expDate.value).set({
                hour: hour24, minute: +this.fields.expMinute.value
            });
            p.expires = expDateTime.toSQL();
        }
        this.submitting = true;
        let obs: Observable<any> = this.promo.id? this.promoService.update(p) : this.promoService.add(p);
        obs.pipe(
            finalize(() => {this.submitting = false})
        ).subscribe((out) => {
            this.state.clear('promo');
            this.router.navigateByUrl('/promos').then();
        }, this.utilService.getErrorHandler('Error saving promo'));
    }

    cancel() {
        this.state.clear('promo');
        this.router.navigateByUrl('/promos').then();
    }

    isPercent(): boolean {
        return this.fields.type.value == 'percent_off';
    }

    expires(): boolean {
        return this.fields.expFlag.value;
    }

    isLimited(): boolean {
        return this.fields.limited.value;
    }
}
